import {
  MpBreadcrumbFilterData,
  MpRowActionParameters,
  MpTable,
} from '@mp-react/table';
import { Box, Stack, Typography } from '@mui/material';
import clsx from 'clsx';
import moment from 'moment';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Row, UseRowSelectRowProps } from 'react-table';
import { ROUTES } from 'config';
import {
  Button,
  DataLossPrompt,
  Footer,
  Loader,
  Menu,
} from 'melp-design/components';
import { useNew } from 'store/news';
import { usePredefinedToasts } from 'utils/Toast';
import { recipientStatuses } from 'types/Recipients';
import InnerWithTabsContainer from '../../../../components/layouts/InnerWithTabs/InnerWithTabsContainer/InnerWithTabsContainer';
import InnerWithTabsLeft from '../../../../components/layouts/InnerWithTabs/InnerWithTabsLeft/InnerWithTabsLeft';
import InnerWithTabsRight from '../../../../components/layouts/InnerWithTabs/InnerWithTabsRight/InnerWithTabsRight';
import InnerWithTabsTitle from '../../../../components/layouts/InnerWithTabs/InnerWithTabsTitle/InnerWithTabsTitle';
import OrganizationUnits from '../../../../components/news/Recipients/OrganizationUnits/OrganizationUnits';
import { useRecipientsTable } from '../../../../configs/Tables/RecipientsTableType';
import {
  LocalPermissionModules,
  PermissionOptions,
} from '../../../../constants/Administrators';
import { HierarchyLevels } from '../../../../constants/ParentCompany';
import { useMe } from '../../../../state/Administrators';
import { useHierarchy } from '../../../../state/Hierarchy';
import {
  useNewsRecipients,
  useNewsRecipientsAsyncMethods,
  useNewsRecipientsStore,
} from '../../../../state/NewsRecipients';
import useTableStyles from '../../../../styles/Table.styles';
import { HierarchyRequest } from '../../../../types/ParentCompany';
import { useNewsRecipientsUtils } from '../../../../utils/NewsRecipients';
import useTableUtils from '../../../../utils/Table';
import { useIdParam } from '../../../../utils/useIdParam';
import useStyles from './Recipients.styles';

interface Props {
  onSaveSuccess: () => void;
}

const Recipients: FC<Props> = ({ onSaveSuccess }) => {
  const { canEditNews } = useMe();
  const classes = useStyles();
  const tableClasses = useTableStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const newsId = useIdParam();
  const predefinedToasts = usePredefinedToasts();
  const { data: newItem } = useNew(newsId);
  const {
    overridables,
    overrideClasses,
    translations,
    handleGetData,
    loadingComponent,
    noDataComponent,
    tableQueryParam,
  } = useTableUtils('recipients');
  const {
    updateAllRecipientsStatuses,
    allSelectionId,
    selectedId,
    handleUnitChange,
    selectedName,
  } = useNewsRecipientsUtils();
  const query = useMemo(() => {
    const selectedOrgQuery = selectedId !== 'all' ? selectedId : '';
    return !!tableQueryParam
      ? `${tableQueryParam}&${selectedId}`
      : selectedOrgQuery;
  }, [selectedId, tableQueryParam]);

  const {
    recipients,
    apiLoading,
    pageSize,
    count,
    resetTable,
    updateRecipients,
    loading,
  } = useNewsRecipients(newsId, query);

  const hierarchyRequest = useMemo<HierarchyRequest>(
    () => ({
      level: HierarchyLevels.COMPANY_AND_COMPANY_GROUPS,
      module: LocalPermissionModules.News,
      permissions: canEditNews
        ? PermissionOptions.Edit
        : PermissionOptions.View,
    }),
    [canEditNews],
  );
  const { hierarchy, loading: hierarchyLoading } =
    useHierarchy(hierarchyRequest);
  const asyncGetMethods = useNewsRecipientsAsyncMethods(newsId);

  const recipientsTableType = useRecipientsTable();

  const changedRowCount = useNewsRecipientsStore(
    (state) => state.changedRowCount,
  );
  const setRecipientRequest = useNewsRecipientsStore(
    (state) => state.setRecipientRequest,
  );

  const isDraft = useMemo<boolean>(
    () => newItem?.status === 'draft',
    [newItem],
  );
  const passedPublishDate = useMemo<boolean>(
    () =>
      newItem?.publishedAt ? moment(newItem.publishedAt).isBefore() : false,
    [newItem],
  );
  const isActiveNews = useMemo(
    () => !isDraft && passedPublishDate,
    [isDraft, passedPublishDate],
  );

  const hasChanges = useMemo(() => changedRowCount > 0, [changedRowCount]);

  const toggleRow = useCallback(
    (row: MpRowActionParameters) => setRecipientRequest({ row }),
    [setRecipientRequest],
  );

  const rowMethods = useMemo(() => ({ toggleRow }), [toggleRow]);

  const handleRowClick = useCallback(
    (current: Row<{}> & UseRowSelectRowProps<{}>) => {
      history.push(
        `${ROUTES.employeeGroups.details.replace(
          ':id',
          current.id,
        )}?tab=innerGroupAssignEmployees`,
      );
    },
    [history],
  );

  const overrideRecipientsTableClasses = useMemo(
    () => ({
      ...overrideClasses,
      tableWrapper: classes.tableWrapper,
    }),
    [classes.tableWrapper, overrideClasses],
  );

  const OrgUnits = useMemo(
    () =>
      !hierarchyLoading ? (
        <OrganizationUnits
          tree={hierarchy as MpBreadcrumbFilterData}
          allSelectionId={allSelectionId}
          onClick={handleUnitChange}
          selectedId={selectedId}
          disabled={false}
        />
      ) : (
        <Loader minHeight={20} />
      ),
    [allSelectionId, handleUnitChange, hierarchy, hierarchyLoading, selectedId],
  );

  return (
    <>
      <Box>
        <Stack marginBottom={2} flexDirection="row" justifyContent="flex-end">
          <Menu
            label={t('permissions.set_all_to')}
            items={recipientStatuses.map((status) => ({
              label: t(`status.${status}`),
              onClick: () => updateAllRecipientsStatuses(recipients, status),
              disabled: isActiveNews,
            }))}
          />
        </Stack>

        <InnerWithTabsContainer>
          <InnerWithTabsRight>
            <InnerWithTabsTitle>{t('recipients.org_units')}</InnerWithTabsTitle>
            {OrgUnits}
          </InnerWithTabsRight>
          <InnerWithTabsLeft>
            <Box mb="22px">
              <Typography variant="h6">{selectedName}</Typography>
            </Box>
            <Box
              className={clsx({
                [classes.table]: true,
                [tableClasses.disableTable]: !canEditNews,
              })}
            >
              <MpTable
                {...recipientsTableType}
                data={recipients}
                onGetData={handleGetData}
                overridables={overridables}
                disablePagination={true}
                classes={overrideRecipientsTableClasses}
                pageSize={pageSize}
                loading={apiLoading}
                rowMethods={rowMethods}
                translations={translations}
                loadingComponent={loadingComponent}
                emptyChildren={noDataComponent}
                dataCount={count}
                asyncGetMethods={asyncGetMethods}
                onRowClick={handleRowClick as any}
              />
            </Box>
          </InnerWithTabsLeft>
        </InnerWithTabsContainer>

        <Footer visible={hasChanges}>
          <Stack direction="row" justifyContent="end" gap="10px">
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                resetTable();
              }}
            />
            <Button
              label={t('common.save')}
              variant="secondary"
              onClick={async () => {
                await updateRecipients();
                predefinedToasts.success.updated();
                onSaveSuccess();
              }}
              disabled={loading}
            />
          </Stack>
        </Footer>
      </Box>

      <DataLossPrompt when={hasChanges} />
    </>
  );
};

export default Recipients;
